import type { Event, EventSettings } from '../services/events';
import type { SongRequest } from '../services/requests';
import { isPlayModeLocked } from './playModeLock';

export const MIN_PARTICIPANTS = 10;
export const RETURN_GAP_POINTS = 10;
export const DEFAULT_VOTE_OUT_THRESHOLD = 50;
export const DEFAULT_UPVOTE_OVERRIDE_PERCENT = 70;
export const VOTE_OUT_THRESHOLD_OPTIONS = [40, 45, 50, 55, 60, 65, 70, 75];
export const UPVOTE_OVERRIDE_OPTIONS = [50, 55, 60, 65, 70, 75, 80, 85, 90];
export const VOTED_OUT_QUEUE_REFUSAL = "Voted out by the crowd — it can't be queued until it's back";

export interface VoteRules {
  downvotingEnabled: boolean;
  voteOutEnabled: boolean;
  thresholdPercent: number;
  upvoteOverrideEnabled: boolean;
  upvoteOverridePercent: number;
  queueMode: string;
}

type CrowdRequest = Pick<SongRequest, 'votedOut' | 'crowdFavorite' | 'downvoteCount' | 'upvoteCount'>;

export function voteRulesOf(settings?: Partial<EventSettings>): VoteRules {
  return {
    downvotingEnabled: settings?.downvotingEnabled === true,
    voteOutEnabled: settings?.voteOutEnabled !== false,
    thresholdPercent: typeof settings?.voteOutThresholdPercent === 'number'
      ? settings.voteOutThresholdPercent : DEFAULT_VOTE_OUT_THRESHOLD,
    upvoteOverrideEnabled: settings?.upvoteOverrideEnabled === true,
    upvoteOverridePercent: typeof settings?.upvoteOverridePercent === 'number'
      ? settings.upvoteOverridePercent : DEFAULT_UPVOTE_OVERRIDE_PERCENT,
    queueMode: settings?.queueMode ?? 'standard',
  };
}

export function voteOutActive(rules: VoteRules): boolean {
  return rules.queueMode !== 'fifo' && rules.downvotingEnabled && rules.voteOutEnabled;
}

export function sharePercent(count: number, participants: number): number {
  // Multiply first: (9 / 20) * 100 is 45.00000000000001, which would count as "above 45%"
  return participants > 0 ? (count * 100) / participants : 0;
}

export function returnLinePercent(rules: VoteRules): number {
  return rules.thresholdPercent - RETURN_GAP_POINTS;
}

export function isVotedOut(request: Pick<SongRequest, 'votedOut'>): boolean {
  return request.votedOut === true;
}

export function isCrowdFavorite(request: Pick<SongRequest, 'crowdFavorite'>): boolean {
  return request.crowdFavorite === true;
}

export function canQueueOrLock(request: Pick<SongRequest, 'votedOut'>): boolean {
  return !isVotedOut(request);
}

export function wouldBeVotedOut(request: CrowdRequest, participantCount: number, rules: VoteRules): boolean {
  return !rules.voteOutEnabled
    && rules.downvotingEnabled
    && rules.queueMode !== 'fifo'
    && participantCount >= MIN_PARTICIPANTS
    && sharePercent(request.downvoteCount ?? 0, participantCount) > rules.thresholdPercent;
}

export function votedOutLabel(request: CrowdRequest, participantCount: number, rules: VoteRules): string {
  if (participantCount <= 0) return 'Voted out';
  const share = Math.round(sharePercent(request.downvoteCount ?? 0, participantCount));
  return `Voted out · ${share}% against · back under ${returnLinePercent(rules)}%`;
}

export function crowdFavoriteLabel(request: CrowdRequest, participantCount: number): string {
  if (participantCount <= 0) return 'Crowd favorite';
  return `Crowd favorite · ${Math.round(sharePercent(request.upvoteCount ?? 0, participantCount))}% upvoted`;
}

export function shouldForceLockOnQueue(
  request: Pick<SongRequest, 'downvoteCount'>,
  settings: Partial<EventSettings> | undefined,
): boolean {
  return voteOutActive(voteRulesOf(settings)) && (request.downvoteCount ?? 0) > 0;
}

export function isVoteRulesLocked(event: Pick<Event, 'playModeLockedAt' | 'requestsStartedAt'> | null | undefined): boolean {
  return isPlayModeLocked(event);
}

// Crowd favorites first (highest upvote share first), voted-out songs last, everything else equal
export function compareCrowdTier(a: CrowdRequest, b: CrowdRequest): number {
  const aOut = isVotedOut(a) ? 1 : 0;
  const bOut = isVotedOut(b) ? 1 : 0;
  if (aOut !== bOut) return aOut - bOut;
  const aFavorite = isCrowdFavorite(a) ? 1 : 0;
  const bFavorite = isCrowdFavorite(b) ? 1 : 0;
  if (aFavorite !== bFavorite) return bFavorite - aFavorite;
  if (aFavorite && bFavorite) return (b.upvoteCount ?? 0) - (a.upvoteCount ?? 0);
  return 0;
}

export interface VoteStats {
  up: number;
  down: number;
  upBefore: number | null;
  upAfter: number | null;
  downBefore: number | null;
  downAfter: number | null;
}

export function splitVoteStats(votes: { type: 'up' | 'down'; votedAt: string | null }[], start: Date | null): VoteStats {
  const up = votes.filter(vote => vote.type === 'up').length;
  const down = votes.length - up;
  if (!start) return { up, down, upBefore: null, upAfter: null, downBefore: null, downAfter: null };

  const startMs = start.getTime();
  const isBefore = (vote: { votedAt: string | null }) =>
    vote.votedAt !== null && new Date(vote.votedAt).getTime() < startMs;
  const upBefore = votes.filter(vote => vote.type === 'up' && isBefore(vote)).length;
  const downBefore = votes.filter(vote => vote.type === 'down' && isBefore(vote)).length;
  return { up, down, upBefore, upAfter: up - upBefore, downBefore, downAfter: down - downBefore };
}

export function votedOutSeenKey(eventId: string, requestId: string): string {
  return `votebeats_votedout_${eventId}_${requestId}`;
}

export function votedBackSeenKey(eventId: string, requestId: string): string {
  return `votebeats_votedback_${eventId}_${requestId}`;
}

export function votedBackToastIds(
  previousOutIds: Set<string>,
  currentOutIds: Set<string>,
  stillQueuedIds: Set<string>,
  seen: (requestId: string) => boolean,
  dontCareIds: Set<string>,
): string[] {
  return [...previousOutIds].filter(id =>
    !currentOutIds.has(id) && stillQueuedIds.has(id) && !seen(id) && !dontCareIds.has(id));
}

export interface VoteRuleValues {
  downvotingEnabled: boolean;
  voteOutEnabled: boolean;
  voteOutThresholdPercent: number;
  upvoteOverrideEnabled: boolean;
  upvoteOverridePercent: number;
}

export function voteRuleValuesOf(settings?: Partial<EventSettings>): VoteRuleValues {
  const rules = voteRulesOf(settings);
  return {
    downvotingEnabled: rules.downvotingEnabled,
    voteOutEnabled: rules.voteOutEnabled,
    voteOutThresholdPercent: rules.thresholdPercent,
    upvoteOverrideEnabled: rules.upvoteOverrideEnabled,
    upvoteOverridePercent: rules.upvoteOverridePercent,
  };
}

export function voteRuleValuesEqual(a: VoteRuleValues, b: VoteRuleValues): boolean {
  return a.downvotingEnabled === b.downvotingEnabled
    && a.voteOutEnabled === b.voteOutEnabled
    && a.voteOutThresholdPercent === b.voteOutThresholdPercent
    && a.upvoteOverrideEnabled === b.upvoteOverrideEnabled
    && a.upvoteOverridePercent === b.upvoteOverridePercent;
}

export function downvoteBadgeTitle(
  request: Pick<SongRequest, 'downvoteCount' | 'upvoteCount'>,
  participantCount: number,
  rules: VoteRules,
): string {
  if (participantCount <= 0) return `${request.upvoteCount} upvotes, ${request.downvoteCount} downvotes`;
  const share = Math.round(sharePercent(request.downvoteCount, participantCount));
  const base = `${request.downvoteCount} of ${participantCount} participants downvoted (${share}%)`;
  const rule = voteOutActive(rules)
    ? ` — out above ${rules.thresholdPercent}%, back under ${returnLinePercent(rules)}%`
    : ' — vote-out is off for this event';
  const minimum = voteOutActive(rules) && participantCount < MIN_PARTICIPANTS
    ? ` · vote-out starts at ${MIN_PARTICIPANTS} participants`
    : '';
  return base + rule + minimum;
}

export function downvoteBadgeText(request: Pick<SongRequest, 'downvoteCount'>, participantCount: number): string {
  return participantCount > 0
    ? `${Math.round(sharePercent(request.downvoteCount, participantCount))}%`
    : String(request.downvoteCount);
}
